import { ErrorApi } from '../identidad/cliente-identidad.js'
import { notificar } from '../fundacion/alertas.js'
import type { Expediente } from '../expedientes/cliente-expedientes.js'
import {
  crearSeguimiento,
  decidirSeguimiento,
  listarNotificaciones,
  listarSeguimiento,
  marcarNotificacionLeida,
  type RegistroSeguimiento,
} from './cliente-seguimiento.js'

type Modulo = 'asesorias' | 'apoyos' | 'diarios'
const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}
const errorTexto = (error: unknown) =>
  error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.'
let expediente: Expediente | null = null
let roles: string[] = []

const modulos: Modulo[] = ['asesorias', 'apoyos', 'diarios']
const etiquetaEstado = (estado: RegistroSeguimiento['estado']) => {
  const dato = document.createElement('span')
  dato.className = `badge ${estado === 'VALIDADO' ? 'bg-success' : estado === 'RECHAZADO' ? 'bg-danger' : 'bg-warning text-dark'}`
  dato.textContent = estado
  return dato
}
const informar = (mensaje: string, tipo = 'success') => {
  const alerta = elemento<HTMLElement>('#alerta-seguimiento')
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}

function puedeRevisar(modulo: Modulo): boolean {
  return modulo === 'apoyos' ? roles.includes('TITULAR') : roles.includes('ASESOR')
}

async function cargarModulo(modulo: Modulo): Promise<void> {
  if (!expediente) return
  const respuesta = await listarSeguimiento(expediente.id, modulo)
  const contenedor = elemento<HTMLElement>(`#lista-${modulo}`)
  contenedor.replaceChildren(
    ...respuesta.datos.map((registro) => {
      const tarjeta = document.createElement('article')
      tarjeta.className = 'card border mb-3'
      const cuerpo = document.createElement('div')
      cuerpo.className = 'card-body'
      const titulo = document.createElement('h3')
      titulo.className = 'h6'
      titulo.textContent =
        registro.asunto ??
        registro.actividad ??
        `Diario del ${new Date(registro.creadoEn).toLocaleDateString('es-CO')}`
      const texto = document.createElement('p')
      texto.className = 'mb-2 texto-con-saltos'
      texto.textContent = registro.compromisoEstudiante ?? registro.analisisReflexivo ?? ''
      const meta = document.createElement('p')
      meta.className = 'small text-muted'
      meta.textContent = `${registro.estudianteNombre} · Revisor: ${registro.revisorNombre}${registro.fecha ? ` · ${registro.fecha} · ${registro.horas} hora(s)` : ''}`
      cuerpo.append(titulo, texto, meta, etiquetaEstado(registro.estado))
      if (registro.motivoRechazo) {
        const motivo = document.createElement('p')
        motivo.className = 'alert alert-danger mt-2 mb-0'
        motivo.textContent = `Motivo: ${registro.motivoRechazo}`
        cuerpo.append(motivo)
      }
      if (registro.estado === 'PENDIENTE' && puedeRevisar(modulo)) {
        const acciones = document.createElement('div')
        acciones.className = 'mt-3 d-flex gap-2'
        for (const decision of ['VALIDAR', 'RECHAZAR'] as const) {
          const boton = document.createElement('button')
          boton.type = 'button'
          boton.className =
            decision === 'VALIDAR' ? 'btn btn-sm btn-success' : 'btn btn-sm btn-outline-danger'
          boton.textContent = decision === 'VALIDAR' ? 'Validar' : 'Rechazar'
          boton.addEventListener('click', () => {
            const motivo =
              decision === 'RECHAZAR'
                ? window.prompt('Motivo del rechazo (máximo 1.000 caracteres):')?.trim()
                : undefined
            if (decision === 'RECHAZAR' && !motivo) return
            void decidirSeguimiento(modulo, registro, decision, motivo)
              .then(() => cargarModulo(modulo))
              .then(() => informar('Decisión registrada correctamente.'))
              .catch((error: unknown) => informar(errorTexto(error), 'danger'))
          })
          acciones.append(boton)
        }
        cuerpo.append(acciones)
      }
      tarjeta.append(cuerpo)
      return tarjeta
    }),
  )
  contenedor.classList.toggle('d-none', false)
}

export async function mostrarSeguimiento(nuevo: Expediente, nuevosRoles: string[]): Promise<void> {
  expediente = nuevo
  roles = nuevosRoles
  elemento('#panel-seguimiento').classList.remove('d-none')
  const pestañaAsesorias = elemento<HTMLButtonElement>('[data-bs-target="#panel-asesorias"]')
  pestañaAsesorias.closest('.nav-item')?.classList.toggle('d-none', roles.includes('TITULAR'))
  elemento('#panel-asesorias').classList.toggle('d-none', roles.includes('TITULAR'))
  if (roles.includes('TITULAR')) {
    elemento<HTMLButtonElement>('[data-bs-target="#panel-apoyos"]').classList.add('active')
    elemento<HTMLButtonElement>('[data-bs-target="#panel-asesorias"]').classList.remove('active')
    elemento('#panel-apoyos').classList.add('show', 'active')
    elemento('#panel-asesorias').classList.remove('show', 'active')
  }
  const puedeCrear = roles.includes('ESTUDIANTE') && nuevo.estado === 'ABIERTO'
  for (const modulo of modulos) {
    elemento(`#formulario-${modulo}`).classList.toggle('d-none', !puedeCrear)
    await cargarModulo(modulo)
  }
  elemento('#panel-seguimiento').scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export async function mostrarNotificaciones(): Promise<void> {
  const respuesta = await listarNotificaciones()
  const lista = elemento<HTMLElement>('#lista-notificaciones')
  lista.replaceChildren(
    ...respuesta.datos.map((notificacion) => {
      const item = document.createElement('button')
      item.type = 'button'
      item.className = `list-group-item list-group-item-action ${notificacion.leidaEn ? '' : 'fw-bold'}`
      item.textContent = `${notificacion.titulo}: ${notificacion.mensaje}`
      item.addEventListener('click', () => {
        void marcarNotificacionLeida(notificacion.id).then(mostrarNotificaciones)
      })
      return item
    }),
  )
  elemento('#panel-notificaciones').classList.remove('d-none')
}

function enlazarFormulario(modulo: Modulo, obtener: () => object): void {
  elemento<HTMLFormElement>(`#formulario-${modulo}`).addEventListener('submit', (evento) => {
    evento.preventDefault()
    const formulario = evento.currentTarget as HTMLFormElement
    formulario.classList.add('was-validated')
    if (!formulario.checkValidity() || !expediente) return
    void crearSeguimiento(expediente.id, modulo, obtener())
      .then(() => {
        formulario.reset()
        formulario.classList.remove('was-validated')
        return cargarModulo(modulo)
      })
      .then(() => informar('Registro creado y enviado a revisión.'))
      .catch((error: unknown) => informar(errorTexto(error), 'danger'))
  })
}

enlazarFormulario('asesorias', () => ({
  asunto: elemento<HTMLInputElement>('#asunto-asesoria').value,
  compromisoEstudiante: elemento<HTMLTextAreaElement>('#compromiso-asesoria').value,
}))
enlazarFormulario('apoyos', () => ({
  fecha: elemento<HTMLInputElement>('#fecha-apoyo').value,
  horas: Number(elemento<HTMLInputElement>('#horas-apoyo').value),
  actividad: elemento<HTMLTextAreaElement>('#actividad-apoyo').value,
}))
enlazarFormulario('diarios', () => ({
  analisisReflexivo: elemento<HTMLTextAreaElement>('#analisis-diario').value,
}))
